/**
 * @file Field Value Components
 * @description Field value utilities and components for handling custom field data
 */

import React from 'react';

/**
 * Field IDs in order for fuel log forms
 * @constant
 * @type {string[]}
 */
export const FIELD_IDS = [
  'a168234f-74c2-477c-95e3-f2a5e00f8694', // Date
  '275e5342-5d0d-4508-8712-82dffa8296ed', // Name
  'e2f5badc-d154-4c71-8ae2-1d8c678e3d7d', // Job Number
  '6421984c-9b70-425e-837c-556d6483cde2', // Equipment Number
  '55dbcda9-45db-4dfd-835b-f32e7e959368', // Fuel Type
  '2af55845-90f1-43e1-8b13-cbca3c68f2d9', // Quantity Issued
  '503a8287-6091-486e-8da4-7521c750b314', // Start Meter Reading
  '056556ec-0514-4e6b-9fdf-da4f234009e0', // End Meter Reading
];

/**
 * Field IDs for Fueling - Daily Fuel Truck template
 * Tank Refill section and Fuel Truck Meters section
 * @constant
 * @type {string[]}
 */
export const DAILY_FUEL_TRUCK_FIELD_IDS = [
  // Tank Refill section
  'bc64a26a-4d96-401d-a324-0a4c65ebad64', // Highway Start (Tank Refill)
  '38a9cc27-1596-4b03-bfe6-9fb843c96896', // Highway End (Tank Refill)
  '29f2cb76-4809-4b9f-b8c3-0b16bd146e2c', // Highway Total (Tank Refill)
  '2819f9c2-a6a3-43f9-88e0-7449b2c43456', // Offroad Start (Tank Refill)
  'b845d79e-8903-4564-8cbb-8f6d94577703', // Offroad End (Tank Refill)
  '10df5abc-ea34-4233-a47d-b41bff9a5003', // Offroad Total (Tank Refill)
  // Fuel Truck Meters section
  '30f21309-4af7-4c99-bd9e-eb43796415ca', // Highway Start (Fuel Truck Meters)
  '779b06ce-b58b-40d1-9f6b-41ad7c853ecd', // Highway End (Fuel Truck Meters)
  'a7eb5420-197a-45be-95cd-5198e98e24f4', // Highway Total (Fuel Truck Meters)
  'c2c06336-6e50-488b-8831-b1721624e64b', // Offroad Start (Fuel Truck Meters)
  '483d27a6-cc1e-4081-9c23-0ff8e034791c', // Offroad End (Fuel Truck Meters)
  '3f6551d8-6624-4f96-8136-0e9af418c1ae', // Offroad Total (Fuel Truck Meters)
];

/**
 * Fuel type options available for selection
 * @constant
 * @type {string[]}
 */
export const FUEL_TYPE_OPTIONS = [
  'D1 = Off Road',
  'D3 = On Road',
  'A3= Gas  '
];

/**
 * Default field ID to name mapping
 * @constant
 * @type {Object.<string, string>}
 */
export const DEFAULT_FIELD_NAME_MAP = {
  // Service Station Fuel Log fields
  '275e5342-5d0d-4508-8712-82dffa8296ed': 'Name',
  'e2f5badc-d154-4c71-8ae2-1d8c678e3d7d': 'Job Number',
  'a168234f-74c2-477c-95e3-f2a5e00f8694': 'Date',
  '6421984c-9b70-425e-837c-556d6483cde2': 'Equipment Number',
  '55dbcda9-45db-4dfd-835b-f32e7e959368': 'Fuel Type',
  '2af55845-90f1-43e1-8b13-cbca3c68f2d9': 'Quantity Issued',
  '503a8287-6091-486e-8da4-7521c750b314': 'Start Meter Reading',
  '056556ec-0514-4e6b-9fdf-da4f234009e0': 'End Meter Reading',
  '706f116f-18b7-4194-8b3e-9a1083401e0a': 'Approval Status',
  'bae17861-3915-4227-a063-6074c845eed2': 'Approved/Rejected On',
  'e3d2e1d4-9158-4be1-a835-81686538b172': 'Approved/Rejected By',
  '9b27488b-93b2-43c6-83ea-0d86b8338791': 'Reason if Rejected',

  // Daily Fuel Truck template - Tank Refill section
  'bc64a26a-4d96-401d-a324-0a4c65ebad64': 'Highway Start (Tank Refill)',
  '38a9cc27-1596-4b03-bfe6-9fb843c96896': 'Highway End (Tank Refill)',
  '29f2cb76-4809-4b9f-b8c3-0b16bd146e2c': 'Highway Total (Tank Refill)',
  '2819f9c2-a6a3-43f9-88e0-7449b2c43456': 'Offroad Start (Tank Refill)',
  'b845d79e-8903-4564-8cbb-8f6d94577703': 'Offroad End (Tank Refill)',
  '10df5abc-ea34-4233-a47d-b41bff9a5003': 'Offroad Total (Tank Refill)',

  // Daily Fuel Truck template - Fuel Truck Meters section
  '30f21309-4af7-4c99-bd9e-eb43796415ca': 'Highway Start (Fuel Truck Meters)',
  '779b06ce-b58b-40d1-9f6b-41ad7c853ecd': 'Highway End (Fuel Truck Meters)',
  'a7eb5420-197a-45be-95cd-5198e98e24f4': 'Highway Total (Fuel Truck Meters)',
  'c2c06336-6e50-488b-8831-b1721624e64b': 'Offroad Start (Fuel Truck Meters)',
  '483d27a6-cc1e-4081-9c23-0ff8e034791c': 'Offroad End (Fuel Truck Meters)',
  '3f6551d8-6624-4f96-8136-0e9af418c1ae': 'Offroad Total (Fuel Truck Meters)',

  // Fueling - Gasoline template
  'ab82979f-a5d2-461c-8292-7d753dd6a7a1': 'Equipment Number',
  'a4715c6c-c6b4-435a-ad66-210dc811c2f6': 'Gasoline Gallons Issued',
  'c78c74d0-1631-412c-a6b9-31f382314b28': 'Equipment Meter Reading',
  'b4f9dcee-bab8-4021-adc3-339c3083679a': 'Remarks',

  // Fueling - Highway Diesel template
  'd9879716-f7ff-4264-a58c-3b6e45b5ee72': 'Equipment Number',
  '7f41f3e7-bf42-42ba-aeca-e7d177f62cba': 'Highway Gallons Issued',
  '64f4a02f-e955-41ef-b5c6-8a08be78a434': 'Gallons DEF Issued',
  '6db5dd5a-2ce5-4796-b6b1-9efd81b87326': 'Engine Hours',
  'a48ba202-2bfb-4f46-af37-95c2eadadfb7': 'Equipment Milage',
  '944fdb7c-c5ca-4814-a911-416c4b7e6113': 'Coolant Gallons',
  '38057452-acc3-4f07-a00e-1b76d8a1a31e': '15/40 Engine Oil quarts',
  '9dba4427-ce17-485d-acd4-889c6072f2f3': 'THF (Tractor Hydraulic Fluid) quarts',
  'b4f8a90a-05f8-4df6-a81d-d7960f582605': '80/90 Gear Oil Quarts',
  'dfbd2654-1321-45ce-8677-fae0e7b2fb75': 'Other Oil Quarts',
  '057332ef-6d3e-45a0-958c-d255ab753615': 'Remarks',

  // Fueling - Off Highway Diesel template
  'd2ac6769-6f5b-4408-872d-54c2e5911752': 'Equipment Number',
  '29579480-a67f-4728-aa1b-f3b24c87f1d0': 'Off Highway Gallons Issued',
  '203748b1-1745-49f5-b0fe-f99bcd79619d': 'Gallons DEF Issued',
  '5d2ceedf-bde4-4480-ba67-146681028062': 'Equipment Meter Reading',
  '47c34c8a-bdc2-40f8-be9f-9d45521f4eca': 'Engine Oil (quarts)',
  'dc0eb5b4-8d67-408b-a2bd-243e6ad09960': 'THF (quarts)',
  '0819fd4b-a3e8-4003-9bbe-74fa08f7229d': '80/90 Oil (quarts)',
  'c0a5a1ac-4706-4ca8-b361-e6d6e807465c': 'Remarks',

  // Equipment Check In template
  '85059379-e0d3-4afa-ba62-9dd284da196b': 'Hours/Mileage',
  'fc828355-6080-43fc-b731-0fe246ab800f': 'Damaged?',
  '5bbcba09-cc6f-4751-a4af-b1460bedb736': 'Condition of Equipment',
};

/**
 * Helper function to extract a specific field value from customValues
 *
 * @function getFieldValue
 * @description Extracts and formats a field value from a report's customValues array.
 * Handles different value types (text, choice, date, number, boolean).
 *
 * @param {Object} report - The field report object
 * @param {Object} report.nativeForm - Native form data
 * @param {Array} report.nativeForm.customValues - Array of custom field values
 * @param {string} fieldId - The ID of the field to extract
 *
 * @returns {string} Formatted field value or '—' if not found/empty
 *
 * @example
 * const dateValue = getFieldValue(report, 'a168234f-74c2-477c-95e3-f2a5e00f8694');
 * // Returns: "1/6/2026" or "—"
 *
 * @example
 * const fuelType = getFieldValue(report, '55dbcda9-45db-4dfd-835b-f32e7e959368');
 * // Returns: "D1 = Off Road" or "—"
 */
export const getFieldValue = (report, fieldId) => {
  const custom = report.nativeForm?.customValues || [];
  const cv = custom.find(c => (c.fieldId || c.id) === fieldId);

  if (!cv) return '—';
  if (cv.textVal !== undefined && cv.textVal !== null && cv.textVal !== '') return cv.textVal;
  if (cv.choiceVal !== undefined && cv.choiceVal !== null && cv.choiceVal !== '') {
    return Array.isArray(cv.choiceVal) ? cv.choiceVal.join(', ') : cv.choiceVal;
  }
  if (cv.dateVal !== undefined && cv.dateVal !== null && cv.dateVal !== '') {
    try {
      const date = new Date(cv.dateVal);
      return date.toLocaleDateString();
    } catch (e) {
      return cv.dateVal;
    }
  }
  if (cv.numberVal !== undefined && cv.numberVal !== null) return String(cv.numberVal);
  if (cv.boolVal !== undefined && cv.boolVal !== null) return cv.boolVal ? 'Yes' : 'No';
  if (cv.toggleVal !== undefined && cv.toggleVal !== null && cv.toggleVal !== '') {
    // Toggle values: 0 = No, 1 = Yes, 2 = NA
    // Also handle string "True"/"False"/"NA"
    if (cv.toggleVal === 1 || cv.toggleVal === '1' || cv.toggleVal === 'True' || cv.toggleVal === true) return 'Yes';
    if (cv.toggleVal === 0 || cv.toggleVal === '0' || cv.toggleVal === 'False' || cv.toggleVal === false) return 'No';
    if (cv.toggleVal === 2 || cv.toggleVal === '2' || cv.toggleVal === 'NA') return 'NA';
    return String(cv.toggleVal);
  }
  return cv.value ?? cv.raw ?? '—';
};

/**
 * Helper function to convert custom value object to display string
 *
 * @private
 * @param {Object} cv - Custom value object
 * @returns {string} Formatted value string
 */
const cvToValue = (cv) => {
  if (!cv) return '—';
  if (cv.textVal !== undefined && cv.textVal !== null && cv.textVal !== '') return cv.textVal;
  if (cv.choiceVal !== undefined && cv.choiceVal !== null && cv.choiceVal !== '') {
    return Array.isArray(cv.choiceVal) ? cv.choiceVal.join(', ') : cv.choiceVal;
  }
  if (cv.dateVal !== undefined && cv.dateVal !== null && cv.dateVal !== '') {
    try {
      const date = new Date(cv.dateVal);
      return date.toLocaleDateString();
    } catch (e) {
      return cv.dateVal;
    }
  }
  if (cv.numberVal !== undefined && cv.numberVal !== null) return String(cv.numberVal);
  if (cv.boolVal !== undefined && cv.boolVal !== null) return cv.boolVal ? 'Yes' : 'No';
  return cv.value ?? cv.raw ?? '—';
};

/**
 * CustomValuesTable Component
 *
 * @component
 * @description Displays custom field values from a report in a formatted table layout.
 * Fields are displayed in a specific order defined by FIELD_IDS.
 *
 * @param {Object} props - Component props
 * @param {Object} props.report - The field report object
 * @param {Object} props.report.nativeForm - Native form data
 * @param {Array} props.report.nativeForm.customValues - Array of custom field values
 * @param {Object.<string, string>} [props.fieldNameMap={}] - Mapping of field IDs to display names
 *
 * @returns {JSX.Element} Formatted display of custom field values
 *
 * @example
 * <CustomValuesTable
 *   report={fieldReport}
 *   fieldNameMap={fieldNameMap}
 * />
 */
export const CustomValuesTable = ({ report, fieldNameMap = {} }) => {
  const custom = report.nativeForm?.customValues || [];

  if (!custom || custom.length === 0) {
    return (
      <div style={{ fontSize: '0.88rem', color: '#64748b', fontStyle: 'italic' }}>
        No field data available
      </div>
    );
  }

  // Create field map for quick lookup
  const fieldMap = {};
  custom.forEach(cv => {
    const fieldId = cv.fieldId || cv.id;
    if (fieldId) fieldMap[fieldId] = cv;
  });

  // Build display parts in order
  const displayParts = [];
  FIELD_IDS.forEach(fieldId => {
    const cv = fieldMap[fieldId];
    const fieldName = fieldNameMap[fieldId] || fieldId;
    const value = cvToValue(cv);
    if (value && value !== '—') {
      displayParts.push(`${fieldName}: ${value}`);
    }
  });

  return (
    <div style={{
      fontSize: '0.88rem',
      lineHeight: '1.6',
      color: '#334155',
      display: 'flex',
      flexWrap: 'wrap',
      gap: '12px'
    }}>
      {displayParts.length > 0 ? displayParts.map((part, idx) => (
        <div key={idx} style={{
          display: 'inline-flex',
          alignItems: 'center',
          whiteSpace: 'nowrap'
        }}>
          <strong style={{ color: '#0f172a', marginRight: '4px' }}>
            {part.split(':')[0]}:
          </strong>
          <span>{part.split(':').slice(1).join(':')}</span>
        </div>
      )) : (
        <div style={{ fontStyle: 'italic', color: '#64748b' }}>
          No field data available
        </div>
      )}
    </div>
  );
};

export default {
  FIELD_IDS,
  DAILY_FUEL_TRUCK_FIELD_IDS,
  FUEL_TYPE_OPTIONS,
  DEFAULT_FIELD_NAME_MAP,
  getFieldValue,
  CustomValuesTable
};
